// ========== 环境配置模块优化版 ========== 
import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { toast } from 'react-hot-toast';

// 环境检测（简化逻辑）
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;
const mode = import.meta.env.MODE;

// API基础URL配置（优化逻辑）[3,5](@ref)
export const API_BASE_URL = (() => {
  // 优先级：显式环境变量 > 开发环境默认值 > 生产环境默认值
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  return isDevelopment 
    ? '/api' 
    : '/api';
})();

// WebSocket配置（关键优化：协议自动切换）[4,8](@ref)
export const SOCKET_URL = (() => {
  // 开发环境
  if (isDevelopment) return 'ws://philiapedia.com';
  
  // 生产环境
  if (isProduction) {
    // 显式配置优先
    if (import.meta.env.VITE_SOCKET_URL) {
      return import.meta.env.VITE_SOCKET_URL;
    }
    
    // 自动生成安全的WebSocket URL（wss协议）
    return `ws://philiapedia.com`; // 使用wss协议和您的正式域名
  }
  
  // 默认回退
  return typeof window !== 'undefined' 
    ? `ws://${window.location.host}`
    : '';
})();

// 全局API配置（简化结构）
export const API_CONFIG = {
  TIMEOUT: 10000,
  HEADERS: { 'Content-Type': 'application/json' }
};

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    // 处理401错误
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      toast.error('登录已过期，请重新登录');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // 处理网络错误
    if (!error.response) {
      toast.error('网络连接失败，请检查网络设置');
      return Promise.reject(error);
    }

    // 处理其他HTTP错误
    const errorMessage = (error.response.data as any)?.message || '请求失败';
    toast.error(errorMessage);

    return Promise.reject(error);
  }
);

// 导出axios实例作为默认导出
export default apiClient;

// 导出便捷的API方法
export const api = {
  get: <T = any>(url: string, config?: any) => 
    apiClient.get<T>(url, config).then(response => response.data),
  
  post: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.post<T>(url, data, config).then(response => response.data),
  
  put: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.put<T>(url, data, config).then(response => response.data),
  
  delete: <T = any>(url: string, config?: any) => 
    apiClient.delete<T>(url, config).then(response => response.data),
  
  patch: <T = any>(url: string, data?: any, config?: any) => 
    apiClient.patch<T>(url, data, config).then(response => response.data),
};

// URL处理工具函数（解决路径拼接问题）[5](@ref)
export const getApiUrl = (endpoint: string): string => {
  const normalizedBase = API_BASE_URL.endsWith('/') 
    ? API_BASE_URL.slice(0, -1) 
    : API_BASE_URL;
  
  const normalizedEndpoint = endpoint.startsWith('/') 
    ? endpoint 
    : `/${endpoint}`;
  
  return `${normalizedBase}${normalizedEndpoint}`;
};

// 带认证的请求头（安全增强）[4](@ref)
export const getAuthHeaders = (): HeadersInit => {
  const token = typeof window !== 'undefined'
    ? localStorage.getItem('token')
    : null;

  return token
    ? { ...API_CONFIG.HEADERS, Authorization: `Bearer ${token}` }
    : API_CONFIG.HEADERS;
};

// ========== 调试输出（优化格式） ==========
console.debug('🛠️ 环境配置', {
  mode,
  isDevelopment,
  isProduction,
  apiBaseUrl: API_BASE_URL,
  socketUrl: SOCKET_URL,
  features: {
    authSupported: typeof localStorage !== 'undefined',
    domain: 'philiapedia.com' // 添加域名标识
  }
});